/* eslint-disable no-restricted-globals */
const dataDownloaderWorker = () => {
  self.addEventListener("message", (event) => {
    if (!event) return;
    const {
      cips,
      cleaningEffectiness,
      fouling,
      benefitsInfo,
      annualBenefitsInfo,
      predictionandRecommendData,
      comparisonMinMaxData,
      predictionDataRaw,
      others,
      totalbenefitWindow,
      annualBenefitsWindow,
      suggestedOthers,
    } = event.data.exportData;
    const { exportWorkerTransaltionString } = event.data.translations;
    const foulingType = event.data.foulingType;

    const FLAG_DISPLAY_ORDER = {
      R: 0,
      W: 1,
      E: 2,
    };

    const normalizeCleaningEffectivenessFlags = (flags) => {
      if (!flags) return [];

      const inputFlags = Array.isArray(flags)
        ? flags
        : typeof flags === "string"
          ? flags.split("")
          : [];

      const normalized = inputFlags
        .map((flag) => String(flag).toUpperCase())
        .filter((flag) => flag === "W" || flag === "R" || flag === "E")
        .sort();

      return Array.from(new Set(normalized));
    };

    const parseCleaningEffectivenessValue = (input) => {
      if (input && typeof input === "object" && "value" in input) {
        const numericValue = parseFloat(input.value);
        return {
          value: input.value,
          numericValue: Number.isNaN(numericValue) ? NaN : numericValue,
          flags: normalizeCleaningEffectivenessFlags(input.flags),
        };
      }

      if (input === null || input === undefined) {
        return { value: input, numericValue: NaN, flags: [] };
      }

      const raw = String(input).trim();
      const match = raw.match(/^(-?[\d.]*)\s*([WRE]*)$/);
      const numericPart = (match?.[1] ?? raw).trim();
      const numericValue = parseFloat(numericPart);

      return {
        value: input,
        numericValue: Number.isNaN(numericValue) ? NaN : numericValue,
        flags: normalizeCleaningEffectivenessFlags(match?.[2] ?? []),
      };
    };

    const formatCleaningEffectivenessValue = (input) => {
      const formatFlagCodes = (flags = []) =>
        flags
          .slice()
          .sort(
            (a, b) =>
              (FLAG_DISPLAY_ORDER[a] ?? Number.MAX_SAFE_INTEGER) -
              (FLAG_DISPLAY_ORDER[b] ?? Number.MAX_SAFE_INTEGER),
          )
          .join(", ");

      const { numericValue, flags } = parseCleaningEffectivenessValue(input);
      const flagSuffix = formatFlagCodes(flags);
      const displayFlag = flagSuffix ? ` ${flagSuffix}` : "";

      if (Number.isNaN(numericValue)) {
        return flagSuffix || "*";
      }

      if (numericValue === -1) {
        return flagSuffix || "-";
      }

      if (numericValue < 0) {
        return `< 0${displayFlag}`;
      }

      if (numericValue > 100) {
        return `> 100${displayFlag}`;
      }

      return `${(Math.floor(numericValue * 10) / 10).toFixed(1)}${displayFlag}`;
    };

    // Workers cannot import this file.
    // Keep this config & tableDataFormatter.js and in sync.
    const DEFAULT_CURRENCY_CODE = "USD";
    const CURRENCY_SUFFIX_CODES = ["Yuan", "RUB", "SAR"];
    const CURRENCY_CONFIG = {
      USD: {
        countryFlagCode: "US",
        displayLabel: "US Dollar ($)",
        symbol: "$",
        languageCode: "en-US",
        currencyCode: "USD",
        conversionRatio: 1.0,
      },
      Yuan: {
        countryFlagCode: "CN",
        displayLabel: "Chinese Yuan Renminbi (CN¥)",
        symbol: "CN¥",
        languageCode: "zh-CN",
        currencyCode: "CNY",
        conversionRatio: 7.18,
      },
      Euro: {
        countryFlagCode: "EU",
        displayLabel: "Euro (€)",
        symbol: "€",
        languageCode: "de-DE",
        currencyCode: "EUR",
        conversionRatio: 0.92,
      },
      JPY: {
        countryFlagCode: "JP",
        displayLabel: "Japanese Yen (¥)",
        symbol: "¥",
        languageCode: "ja-JP",
        currencyCode: "JPY",
        conversionRatio: 151.45,
      },
      BRL: {
        countryFlagCode: "BR",
        displayLabel: "Brazilian Real (R$)",
        symbol: "R$",
        languageCode: "pt-BR",
        currencyCode: "BRL",
        conversionRatio: 5.39,
      },
      ARS: {
        countryFlagCode: "AR",
        displayLabel: "Peso (Arg$)",
        symbol: "Arg$",
        languageCode: "es-AR",
        currencyCode: "ARS",
        conversionRatio: 915.15,
      },
      AUD: {
        countryFlagCode: "AU",
        displayLabel: "Australian Dollar (AU$)",
        symbol: "AU$",
        languageCode: "en-AU",
        currencyCode: "AUD",
        conversionRatio: 1.52,
      },
      CAD: {
        countryFlagCode: "CA",
        displayLabel: "Canadian Dollar (CA$)",
        symbol: "CA$",
        languageCode: "en-CA",
        currencyCode: "CAD",
        conversionRatio: 1.37,
      },
      INR: {
        countryFlagCode: "IN",
        displayLabel: "Indian Rupee (₹)",
        symbol: "₹",
        languageCode: "en-IN",
        currencyCode: "INR",
        conversionRatio: 83.68,
      },
      IDR: {
        countryFlagCode: "ID",
        displayLabel: "Indonesian Rupiah (Rp)",
        symbol: "Rp",
        languageCode: "id-ID",
        currencyCode: "IDR",
        conversionRatio: 15866.0,
      },
      KRW: {
        countryFlagCode: "KR",
        displayLabel: "South Korean Won (KRW)",
        symbol: "KRW",
        languageCode: "ko-KR",
        currencyCode: "KRW",
        conversionRatio: 1363.0,
      },
      MXN: {
        countryFlagCode: "MX",
        displayLabel: "Mexican Peso (Mex$)",
        symbol: "Mex$",
        languageCode: "es-MX",
        currencyCode: "MXN",
        conversionRatio: 18.32,
      },
      RUB: {
        countryFlagCode: "RU",
        displayLabel: "Russian Ruble (₽)",
        symbol: "₽",
        languageCode: "ru-RU",
        currencyCode: "RUB",
        conversionRatio: 92.82,
      },
      SAR: {
        countryFlagCode: "SA",
        displayLabel: "Saudi Riyal (SAR)",
        symbol: "SAR",
        languageCode: "ar-SA",
        currencyCode: "SAR",
        conversionRatio: 3.75,
      },
      ZAR: {
        countryFlagCode: "ZA",
        displayLabel: "South African Rand (R)",
        symbol: "R",
        languageCode: "en-ZA",
        currencyCode: "ZAR",
        conversionRatio: 18.33,
      },
      TRY: {
        countryFlagCode: "TR",
        displayLabel: "Turkish Lira (₺)",
        symbol: "₺",
        languageCode: "tr-TR",
        currencyCode: "TRY",
        conversionRatio: 32.88,
      },
      GBP: {
        countryFlagCode: "GB",
        displayLabel: "Pound Sterling (£)",
        symbol: "£",
        languageCode: "en-GB",
        currencyCode: "GBP",
        conversionRatio: 0.78,
      },
      PHP: {
        countryFlagCode: "PH",
        displayLabel: "Philippine Peso (Php)",
        symbol: "Php",
        languageCode: "en-PH",
        currencyCode: "PHP",
        conversionRatio: 56.0,
      },
      SGD: {
        countryFlagCode: "SG",
        displayLabel: "Singapore Dollar (SGD)",
        symbol: "SGD",
        languageCode: "en-SG",
        currencyCode: "SGD",
        conversionRatio: 1.4,
      },
    };

    const multipleReasonFormatter = (type) => {
      if (typeof type === "string") {
        type = type.split(",");
      }
      if (Array.isArray(type)) {
        const formattedType = type.map((item) => {
          const trimmedItem = item.trim();
          return trimmedItem;
        });
        const resultString = formattedType.join(", ");
        return resultString;
      }
      return type;
    };

    const reasonFormatter = (data, isPrimary = true) => {
      if (data?.length === 0) {
        return exportWorkerTransaltionString.noReason;
      } else {
        const { primary_reason, proportion1, secondary_reason, proportion2 } =
          data[0];
        const type = isPrimary ? primary_reason : secondary_reason;
        if (type) {
          const foulingTypeData = foulingType ? foulingType[type] : type;
          const propValue = parseFloat(isPrimary ? proportion1 : proportion2);
          const proportion =
            propValue && propValue > 0
              ? propValue < 10 && propValue % 1 !== 0
                ? `${propValue.toFixed(1)}%`
                : `${Math.round(propValue)}%`
              : "-";
          return `${foulingTypeData} (${proportion})`;
        } else {
          return "-";
        }
      }
    };

    const pressureFormatter = (value) => {
      return formatCleaningEffectivenessValue(value);
    };

    const formatFlagCodes = (rawValue) => {
      const parsed = parseCleaningEffectivenessValue(rawValue);
      const flags = parsed?.flags || [];
      if (!flags.length) return "";

      return flags
        .slice()
        .sort(
          (a, b) =>
            (FLAG_DISPLAY_ORDER[a] ?? Number.MAX_SAFE_INTEGER) -
            (FLAG_DISPLAY_ORDER[b] ?? Number.MAX_SAFE_INTEGER),
        )
        .join(", ");
    };

    const formatUnitForDisplayLocal = (unitValue) => {
      if (typeof unitValue !== "string") return unitValue;

      const unitDisplayMap = {
        "uS/cm": "μS/cm",
        C: "°C",
        F: "°F",
      };
      const trimmedValue = unitValue.trim();
      if (unitDisplayMap[trimmedValue]) return unitDisplayMap[trimmedValue];

      return trimmedValue
        .split("/")
        .map((unitPart) => unitDisplayMap[unitPart.trim()] || unitPart.trim())
        .join("/");
    };

    const formatCleaningEffCellText = (rawValue) => {
      const flagText = formatFlagCodes(rawValue);
      return flagText || pressureFormatter(rawValue);
    };

    const dateFormatter = (date) => {
      if (date) {
        date = new Date(date);

        if (isNaN(date.getTime())) {
          return "Invalid Date";
        }
        const formattedDay = date.getDate().toString().padStart(2, "0");
        const formattedMonth = (date.getMonth() + 1)
          .toString()
          .padStart(2, "0");
        const formattedYear = date.getFullYear();

        return `${formattedDay}/${formattedMonth}/${formattedYear}`;
      } else return null;
    };

    const EVENT_TYPE_FORMATTER_STRINGS = {
      CIP: "cip",
      SHUTDOWN: "shutdown",
      C: "c",
      S: "s",
      SHUTDOWN_AS_PREDICTED: "shutdown (as predicted)",
      SHUTDOWN_WITH_CIP: "shutdown with cip",
    };

    const typeFormatter = (type) => {
      if (!type) {
        return "";
      }

      const lowerValue = type.toLowerCase();

      switch (lowerValue) {
        case EVENT_TYPE_FORMATTER_STRINGS.C:
        case EVENT_TYPE_FORMATTER_STRINGS.CIP:
          return exportWorkerTransaltionString.cip;
        case EVENT_TYPE_FORMATTER_STRINGS.S:
        case EVENT_TYPE_FORMATTER_STRINGS.SHUTDOWN:
          return exportWorkerTransaltionString.shutdown;
        case EVENT_TYPE_FORMATTER_STRINGS.SHUTDOWN_AS_PREDICTED:
          return exportWorkerTransaltionString.shutdownAsPredicted;
        case EVENT_TYPE_FORMATTER_STRINGS.SHUTDOWN_WITH_CIP:
          return exportWorkerTransaltionString.shutdownWithCip;
        default:
          return "";
      }
    };

    // Workers cannot import this file.
    // Keep this function & tableDataFormatter.js and in sync.
    const formattedCurrencyValue = (value) => {
      const currency = others[0].currency;
      const currencyMeta =
        CURRENCY_CONFIG[currency] || CURRENCY_CONFIG[DEFAULT_CURRENCY_CODE];

      const currencyLanguage = currencyMeta.languageCode;
      const currencySymbol = currencyMeta.symbol;
      const isSuffixCurrency = CURRENCY_SUFFIX_CODES.includes(currency);

      if (!value) {
        return isSuffixCurrency
          ? `${value}${currencySymbol}`
          : `${currencySymbol}${value}`;
      }

      const formattedValue = parseFloat(value).toLocaleString(
        currencyLanguage,
        {
          style: "decimal",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      );

      if (isSuffixCurrency) {
        return `${formattedValue}${currencySymbol}`;
      }

      return `${currencySymbol}${formattedValue}`;
    };

    const formatDecimal = (value) => {
      value = parseFloat(value);
      return value.toFixed(2);
    };

    const formatVolumeWithUnit = (value) => {
      const volume = others[0]?.volume;
      const unit = volume === "m3" ? "m³" : volume;
      const formattedValue = formatDecimal(value);
      return `${formattedValue} ${unit}`;
    };

    const cipSheetData = cips?.map((data, index) => {
      return {
        ...data,
        index: index + 1,
        start_date: dateFormatter(data.start_date),
        end_date: dateFormatter(data.end_date),
        type: typeFormatter(data.type),
        cipSequence: data.cip_sequence,
        is_tool_identified: data.is_tool_identified
          ? exportWorkerTransaltionString.calculation
          : exportWorkerTransaltionString.user,
        acidPh: data.acid_cip_ph,
        acidTemperature: data.acid_cip_temp,
        acidDuration: data.acid_cip_duration,
        acidUsed: data.acid_used_per_cip,
        alkaliPh: data.alkali_cip_ph,
        alkaliTemperature: data.alkali_cip_temp,
        alkaliDuration: data.alkali_cip_duration,
        alkaliUsed: data.alkali_used_per_cip,
        additiveAlkaliPh: data.additive_alkali_ph,
        additiveAlkaliTemperature: data.additive_alkali_temp,
        additiveAlkaliDuration: data.additive_alkali_duration,
        additiveAlkaliUsed: data.additive_alkali_used_per_cip,
        additiveAcidPh: data.additive_acid_ph,
        additiveAcidTemperature: data.additive_acid_temp,
        additiveAcidDuration: data.additive_acid_duration,
        additiveAcidUsed: data.additive_acid_used_per_cip,
        acidChemical: data.acid_chemical_name,
        acidPrice: data.acid_price,
        alkaliChemical: data.alkali_chemical_name,
        alkaliPrice: data.alkali_price,
        additiveAlkaliChemical: data.additive_alkali_chemical_name,
        additiveAlkaliPrice: data.additive_alkali_price,
        additiveAcidChemical: data.additive_acid_chemical_name,
        additiveAcidPrice: data.additive_acid_price,
        addInfo: data.add_info,
      };
    });

    const cleaningEffectinessSheetData = cleaningEffectiness?.map(
      (data, index) => {
        const pressureParsed = parseCleaningEffectivenessValue(
          data.pressure_drop,
        );
        const flowParsed = parseCleaningEffectivenessValue(data.flow);
        const saltParsed = parseCleaningEffectivenessValue(data.salt_passage);

        return {
          ...data,
          index: index + 1,
          type: typeFormatter(data.type),
          start_date: dateFormatter(data.start_date),
          pressure_drop: formatCleaningEffCellText(data.pressure_drop),
          flow: formatCleaningEffCellText(data.flow),
          salt_passage: formatCleaningEffCellText(data.salt_passage),
          pressure_drop_flags: pressureParsed.flags,
          flow_flags: flowParsed.flags,
          salt_passage_flags: saltParsed.flags,
          reason: multipleReasonFormatter(data.reason),
        };
      },
    );

    const foulingSheetData = fouling?.map((data, index) => {
      return {
        ...data,
        index: index + 1,
        type: typeFormatter(data.data[0].type),
        start_date: dateFormatter(data.start_date),
        end_date: dateFormatter(data.end_date),
        primary_reason: reasonFormatter(data?.data, true),
        secondary_reason: reasonFormatter(data?.data, false),
      };
    });

    const otherSheetData = (others || []).map((data) => ({
      ...data,
      feed: formatUnitForDisplayLocal(data?.feed),
      permeate: formatUnitForDisplayLocal(data?.permeate),
    }));
    const predicitionSheetData = predictionandRecommendData?.map(
      (data, index) => {
        const predictedItem = data.predictedItem;
        const recommendedItem = data.recommendedItem;
        return {
          predictIndex: predictedItem.start_date ? index + 1 : "",
          predictType: typeFormatter(predictedItem.type),
          predictStart_date: dateFormatter(predictedItem.start_date),
          predictReason: multipleReasonFormatter(predictedItem.reason),
          recommendIndex: recommendedItem.start_date ? index + 1 : "",
          recommendType: typeFormatter(recommendedItem.type),
          recommendStart_date: dateFormatter(recommendedItem.start_date),
          recommendReason: multipleReasonFormatter(
            recommendedItem.reason_recommended,
          ),
        };
      },
    );

    let benefitSheetDataMessage = "";
    let benefitSheetData = null;
    const { show, data } = benefitsInfo;

    if (show) {
      benefitSheetDataMessage = `${
        exportWorkerTransaltionString[data.benefitsLabel]
      } (${totalbenefitWindow}) : ${data.benefitsValue}`;

      benefitSheetData = [
        {
          financialEnergy: formatDecimal(
            predictionDataRaw?.energy_saving_percent,
          ),
          financialEnergyCost: formattedCurrencyValue(
            predictionDataRaw?.energy_saving_dollars,
          ),
          financialChemical: formatDecimal(
            predictionDataRaw?.chemical_saving_percent,
          ),
          financialChemicalCost: formattedCurrencyValue(
            predictionDataRaw?.chemical_saving_dollars,
          ),
          financialDownTime: formatDecimal(
            predictionDataRaw?.downtime_saving_percent,
          ),
          financialDownTimeCost: formattedCurrencyValue(
            predictionDataRaw?.downtime_saving_dollars,
          ),
          financialDownTimeDays: formatDecimal(
            predictionDataRaw?.downtime_saving_days,
          ),
          financialIncreasedProduction: formatDecimal(
            predictionDataRaw?.increased_production_percent,
          ),
          financialIncreasedProductionCost: formattedCurrencyValue(
            predictionDataRaw?.increased_production_dollars,
          ),
          financialIncreasedProductionVolume: formatVolumeWithUnit(
            predictionDataRaw?.increased_production,
          ),
        },
      ];
    }

    let annualBenefitSheetDataMessage = "";
    let annualBenefitSheetData = null;
    const { show: annualShow, data: annualData } = annualBenefitsInfo;

    if (
      predictionDataRaw?.forcast_window < 365 &&
      predictionDataRaw?.forcast_window > 183 &&
      annualShow
    ) {
      annualBenefitSheetDataMessage = `${
        exportWorkerTransaltionString[annualData.benefitsLabel]
      } (${annualBenefitsWindow}) : ${annualData.benefitsValue}`;

      annualBenefitSheetData = [
        {
          financialEnergy: formatDecimal(
            predictionDataRaw?.annual_energy_saving_percent,
          ),
          financialEnergyCost: formattedCurrencyValue(
            predictionDataRaw?.annual_energy_saving_dollars,
          ),
          financialChemical: formatDecimal(
            predictionDataRaw?.annual_chemical_saving_percent,
          ),
          financialChemicalCost: formattedCurrencyValue(
            predictionDataRaw?.annual_chemical_saving_dollars,
          ),
          financialDownTime: formatDecimal(
            predictionDataRaw?.annual_downtime_saving_percent,
          ),
          financialDownTimeCost: formattedCurrencyValue(
            predictionDataRaw?.annual_downtime_saving_dollars,
          ),
          financialDownTimeDays: formatDecimal(
            predictionDataRaw?.annual_downtime_saving_days,
          ),
          financialIncreasedProduction: formatDecimal(
            predictionDataRaw?.annual_increased_production_percent,
          ),
          financialIncreasedProductionCost: formattedCurrencyValue(
            predictionDataRaw?.annual_increased_production_dollars,
          ),
          financialIncreasedProductionVolume: formatVolumeWithUnit(
            predictionDataRaw?.annual_increased_production,
          ),
        },
      ];
    }

    const comparisonSheetData =
      Array.isArray(comparisonMinMaxData) && comparisonMinMaxData.length > 0
        ? comparisonMinMaxData
        : null;

    const comparisonSheetDataMessage = comparisonSheetData
      ? exportWorkerTransaltionString.comparisonWithThresholdTitle
      : "";

    // processedDataSheetData is now pre-processed before being sent to worker
    const { processedDataSheetData } = event.data.exportData;
    const suggestedOtherSheetData = suggestedOthers || null;

    postMessage({
      predicitionSheetData,
      benefitSheetDataMessage,
      annualBenefitSheetDataMessage,
      benefitSheetData,
      annualBenefitSheetData,
      comparisonSheetData,
      comparisonSheetDataMessage,
      cipSheetData,
      cleaningEffectinessSheetData,
      foulingSheetData,
      otherSheetData,
      suggestedOtherSheetData,
      processedDataSheetData,
    });
  });
};

export default dataDownloaderWorker;
